import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useFormatting } from '@ayeldo/frontend-core/hooks/useFormatting';

import { useIdpSession } from '../../hooks/useIdpSession';
import { APIError, idpApi } from '../../services/idp-api';

function getDeleteErrorMessage(error: unknown, t: TFunction<'idp'>): string {
  if (!(error instanceof APIError)) {
    return t('deleteAccount.errors.default');
  }

  return error.message;
}

export function DeleteAccountPage() {
  const { t } = useTranslation('idp');
  const navigate = useNavigate();
  const { formatDateTime } = useFormatting();
  const { isAuthenticated, isLoading } = useIdpSession();
  const [confirmation, setConfirmation] = useState('');
  const [deletionDate, setDeletionDate] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth/signin', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  async function handleRequestDeletion() {
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsSubmitting(true);

    try {
      const response = await idpApi.requestDeleteAccount({ confirmation: 'DELETE' });
      setDeletionDate(response.deletionDate);
      setSuccessMessage(response.message);
      setConfirmation('');
    } catch (error) {
      setErrorMessage(getDeleteErrorMessage(error, t));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCancelDeletion() {
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsCancelling(true);

    try {
      const response = await idpApi.cancelDeletion();
      setDeletionDate(null);
      setSuccessMessage(response.message);
    } catch (error) {
      setErrorMessage(getDeleteErrorMessage(error, t));
    } finally {
      setIsCancelling(false);
    }
  }

  async function handleExportData() {
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsExporting(true);

    try {
      const payload = await idpApi.exportAccountData();
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'ayeldo-account-export.json';
      anchor.click();
      URL.revokeObjectURL(url);
      setSuccessMessage(t('deleteAccount.exportSuccess'));
    } catch (error) {
      setErrorMessage(getDeleteErrorMessage(error, t));
    } finally {
      setIsExporting(false);
    }
  }

  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h1" fontWeight={700}>
        {t('deleteAccount.title')}
      </Typography>

      {successMessage !== null ? <Alert severity="success">{successMessage}</Alert> : null}
      {errorMessage !== null ? <Alert severity="error">{errorMessage}</Alert> : null}

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="body1">{t('deleteAccount.gracePeriodText')}</Typography>
            {deletionDate !== null ? (
              <Alert severity="warning">
                {t('deleteAccount.scheduledWarning', { date: formatDateTime(deletionDate) })}
              </Alert>
            ) : null}
            <TextField
              label={t('deleteAccount.confirmLabel')}
              value={confirmation}
              onChange={(event) => {
                setConfirmation(event.target.value);
              }}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="error"
                disabled={isSubmitting || confirmation !== 'DELETE'}
                onClick={() => {
                  void handleRequestDeletion();
                }}
              >
                {isSubmitting
                  ? t('deleteAccount.schedulingButton')
                  : t('deleteAccount.scheduleButton')}
              </Button>
              <Button
                variant="outlined"
                disabled={isCancelling || deletionDate === null}
                onClick={() => {
                  void handleCancelDeletion();
                }}
              >
                {isCancelling
                  ? t('deleteAccount.cancellingButton')
                  : t('deleteAccount.cancelButton')}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6" component="h2" fontWeight={600}>
              {t('deleteAccount.exportTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('deleteAccount.exportDescription')}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                void handleExportData();
              }}
              disabled={isExporting}
            >
              {isExporting ? t('deleteAccount.exportingButton') : t('deleteAccount.exportButton')}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Link component={RouterLink} to="/account" underline="hover">
        {t('deleteAccount.backToDashboard')}
      </Link>
    </Stack>
  );
}
